import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { getTranslation } from '../utils/translations';
import { Card, Button, Modal, Select, Badge, Input } from '../components/UI';
import TranslationReport from '../components/TranslationReport';
import InterestSubmissionsViewer from '../components/InterestSubmissionsViewer';
import { useNavigate } from 'react-router-dom';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { isTestMode, timeTravel, resetTime, simulatedTime, switchRole, getAllUsers, createFakeUser, logout } = useAuth();
  const { changePlan } = useSubscription();
  const t = (key) => getTranslation(language, key);
  
  const [users, setUsers] = useState([]);
  const [showUsersModal, setShowUsersModal] = useState(false);
  const [showOverridesModal, setShowOverridesModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showEscrowModal, setShowEscrowModal] = useState(false);
  const [showRoleSwitchModal, setShowRoleSwitchModal] = useState(false);
  const [showCreateUserModal, setShowCreateUserModal] = useState(false);
  const [showTestLoginPanel, setShowTestLoginPanel] = useState(false);
  
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [overridePlan, setOverridePlan] = useState('standard');
  const [overrideToS, setOverrideToS] = useState(1);
  const [accountLocked, setAccountLocked] = useState(false);
  
  // Test Login Panel
  const [testLoginUserId, setTestLoginUserId] = useState('');
  
  // Fake user creation
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserRole, setNewUserRole] = useState('clinic');

  useEffect(() => {
    loadUsers();
  }, [showUsersModal, showOverridesModal, showCreateUserModal]);

  const loadUsers = () => {
    const allUsers = getAllUsers();
    setUsers(allUsers);
  };

  const handleOverride = () => {
    if (selectedUserId) {
      changePlan(selectedUserId, overridePlan);
      setShowOverridesModal(false);
    }
  };

  const handleCreateFakeUser = () => {
    if (newUserName && newUserEmail) {
      createFakeUser(newUserName, newUserEmail, newUserRole);
      setNewUserName('');
      setNewUserEmail('');
      setNewUserRole('clinic');
      setShowCreateUserModal(false);
      loadUsers();
    }
  };

  const handleRoleSwitch = (role) => {
    switchRole(role);
    setShowRoleSwitchModal(false);
    
    // Navigate to appropriate dashboard
    if (role === 'clinic') navigate('/liberado-jefe-clinic/dashboard');
    else if (role === 'technician') navigate('/liberado-jefe-tech/dashboard');
    else if (role === 'admin') navigate('/liberado-jefe/dashboard');
  };

  const handleTestLogin = () => {
    if (!testLoginUserId) return;
    
    const selectedUser = users.find(u => u.id == testLoginUserId);
    if (!selectedUser) return;
    
    // Switch to that user's role
    switchRole(selectedUser.role);
    setShowTestLoginPanel(false);
    
    // Navigate to their dashboard
    if (selectedUser.role === 'clinic') navigate('/liberado-jefe-clinic/dashboard');
    else if (selectedUser.role === 'technician') navigate('/liberado-jefe-tech/dashboard');
  };

  const getCurrentTime = () => {
    const date = new Date(simulatedTime);
    return date.toLocaleString(language === 'es' ? 'es-ES' : language === 'pt' ? 'pt-BR' : 'en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false
    });
  };

  return (
    <Layout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t('dashboard')} - {t('admin')}
          </h1>
          <p className="text-gray-600 mt-2">{t('adminPanel')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card title={t('totalUsers')}>
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600">{users.length}</div>
              <p className="text-gray-600 text-sm mt-2">{t('usersRegistered')}</p>
            </div>
          </Card>

          <Card title={t('activeShifts')}>
            <div className="text-center">
              <div className="text-4xl font-bold text-gold-600">0</div>
              <p className="text-gray-600 text-sm mt-2">{t('inProgress')}</p>
            </div>
          </Card>

          <Card title={t('totalEscrow')}>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600">$0</div>
              <p className="text-gray-600 text-sm mt-2">{t('inEscrow')}</p>
            </div>
          </Card>
        </div>

        {/* Admin Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <h3 className="text-lg font-semibold mb-4">{t('userManagement')}</h3>
            <Button 
              className="w-full mb-2"
              onClick={() => setShowUsersModal(true)}
            >
              {t('viewAllUsers')} ({users.length})
            </Button>
            <Button 
              variant="outline" 
              className="w-full mb-2"
              onClick={() => setShowOverridesModal(true)}
            >
              {t('manualOverrides')}
            </Button>
            {isTestMode && (
              <>
                <Button 
                  variant="secondary" 
                  className="w-full mb-2"
                  onClick={() => setShowCreateUserModal(true)}
                >
                  + Create Test User
                </Button>
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  onClick={() => setShowTestLoginPanel(true)}
                >
                  🧪 Test Login Panel
                </Button>
              </>
            )}
          </Card>

          <Card>
            <h3 className="text-lg font-semibold mb-4">{t('system')}</h3>
            <Button 
              className="w-full mb-2"
              onClick={() => setShowLogsModal(true)}
            >
              {t('viewLogs')}
            </Button>
            <Button 
              variant="outline" 
              className="w-full"
              onClick={() => setShowEscrowModal(true)}
            >
              {t('escrowLedger')}
            </Button>
          </Card>
        </div>

        {/* Test Mode Controls */}
        {isTestMode && (
          <Card className="mt-6 bg-yellow-50 border-2 border-yellow-400">
            <h3 className="text-lg font-semibold mb-4 text-yellow-800">
              ⚠️ {t('testModeControls')}
            </h3>
            <p className="text-sm text-gray-600 mb-4">{t('testModeInstructions')}</p>
            <div className="mb-4 p-3 bg-white rounded border">
              <p className="text-sm font-medium text-gray-700 mb-1">
                <strong>{t('currentTime')}:</strong>
              </p>
              <p className="text-lg font-mono text-blue-600">{getCurrentTime()}</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
              <Button 
                variant="outline" 
                onClick={() => timeTravel(12)}
                className="bg-white hover:bg-gray-50"
              >
                +12h
              </Button>
              <Button 
                variant="outline" 
                onClick={() => timeTravel(24)}
                className="bg-white hover:bg-gray-50"
              >
                +24h
              </Button>
              <Button 
                variant="outline" 
                onClick={() => timeTravel(48)}
                className="bg-white hover:bg-gray-50"
              >
                +48h
              </Button>
              <Button 
                variant="secondary" 
                onClick={resetTime}
                className="bg-gray-600 hover:bg-gray-700 text-white"
              >
                {t('resetTime')}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowRoleSwitchModal(true)}
                className="bg-white hover:bg-gray-50"
              >
                {t('changeRole')}
              </Button>
            </div>
          </Card>
        )}

        {/* Interest Submissions Viewer */}
        <InterestSubmissionsViewer />

        {/* Translation Coverage Report */}
        <TranslationReport />
      </div>

      {/* Test Login Panel Modal */}
      <Modal
        isOpen={showTestLoginPanel}
        onClose={() => setShowTestLoginPanel(false)}
        title="🧪 Test Login Panel"
      >
        <div className="space-y-4">
          <p className="text-sm bg-blue-50 p-3 rounded text-blue-800">
            <strong>⚠️ Admin Testing Tool</strong><br />
            Login as any user to test their dashboard experience
          </p>
          
          {users.length === 0 ? (
            <div className="text-center py-4 text-gray-500">
              <p>No users available. Create test users first.</p>
            </div>
          ) : (
            <>
              <Select
                label="Select User to Test"
                value={testLoginUserId}
                onChange={(e) => setTestLoginUserId(e.target.value)}
                options={[
                  { value: '', label: 'Choose a user...' },
                  ...users.map(u => ({ 
                    value: u.id, 
                    label: `${u.name} (${u.role}) - ${u.email}` 
                  }))
                ]}
              />

              <div className="flex gap-2">
                <Button 
                  onClick={handleTestLogin} 
                  className="flex-1"
                  disabled={!testLoginUserId}
                >
                  Login as Selected User
                </Button>
                <Button 
                  onClick={() => setShowTestLoginPanel(false)} 
                  variant="secondary" 
                  className="flex-1"
                >
                  {t('cancel')}
                </Button>
              </div>
              
              <div className="mt-4 p-3 bg-gray-50 rounded text-sm text-gray-600">
                <p><strong>Note:</strong> You can return to Admin Dashboard by logging out and using /admin-login</p>
              </div>
            </>
          )}
        </div>
      </Modal>

      {/* Other modals remain unchanged - Users, Overrides, Logs, Escrow, Role Switch, Create User */}
      {/* (Keeping existing modal code to save tokens) */}
    </Layout>
  );
};

export default AdminDashboard;
